import React, { useEffect, useRef } from 'react';
import useWaBooleanProps from './useWaBooleanProps';

// React 18 does not attach listeners for a custom element's own events: the
// Web Awesome <wa-switch> fires a native `change` event on toggle, but React's
// onChange synthetic-event plumbing is scoped to built-in form controls and
// never wires it up on the custom element — so an `onWaChange`/`onChange` prop
// is silently dead (Review 3 Batch B, #6). Attach the listener explicitly via a
// ref, the same pattern App.js uses for the drawer's `wa-after-hide`.
//
// Controlled: `checked` (and `disabled`/`required`) are driven onto the element as
// *properties* via useWaBooleanProps, so optimistic updates/rollbacks reflect visually
// and a `false` boolean actually clears the state. They are pulled out of `{...rest}`
// so they never reach JSX as attributes (the React-18 boolean-attribute trap — see
// useWaBooleanProps.js). Setting a property programmatically does not emit `change`,
// so there's no feedback loop.
function WaSwitch({ checked, disabled, required, onToggle, children, ...rest }) {
	const ref = useRef(null);

	useEffect(() => {
		const el = ref.current;
		if (!el) return undefined;
		const handler = (e) => onToggle?.(e.target.checked);
		el.addEventListener('change', handler);
		return () => el.removeEventListener('change', handler);
	}, [onToggle]);

	useWaBooleanProps(ref, { checked, disabled, required });

	return (
		<wa-switch ref={ref} {...rest}>
			{children}
		</wa-switch>
	);
}

export default WaSwitch;
